import React, { createRef } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import {
  Box,
  Card,
  Typography,
  Icon,
  NewButton as Button,
} from "@bca/ui-library";
import { AnalyticsProps } from "@bca/ui-library/dist/analytics.types";

const StyledCard = styled(Card)<{ hoverBorderEnabled: boolean }>`
  button {
    width: 146px;
    padding: 0 15px;

    span {
      font-size: 13px;
    }
  }

  @media (hover: hover) {
    ${css({
      "&:hover": {
        position: "relative",
        bottom: "4px",
      },
    })}
  }

  ${css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    p: 6,
    m: 0,
    boxShadow: "none",
    border: "1px solid #E4E6E8",
  })}
  ${({ hoverBorderEnabled }) =>
    hoverBorderEnabled && ":hover {box-shadow: 0px 2px 30px 0px #ced3d6}"};
`;

const Anchor = styled.a`
  text-decoration: none;
`;

interface Analytics extends AnalyticsProps {
  eventName: string;
  eventCategory?: string;
  eventLabel?: string;
}

interface DetailedCardProps {
  icon: "flag" | "file-invoice" | "truck" | "key" | "location-dot";
  iconColor?: string;
  link: string;
  headline: string;
  children: any;
  label: string;
  ariaLabel: string;
  showLinkAsButton?: boolean;
  width?: string;
  hoverBorderEnabled?: boolean;
  additionalOnClickHandler?: any;
  analytics?: Analytics;
}

export default function DetailedCard({
  icon,
  link,
  headline,
  children,
  label,
  ariaLabel,
  showLinkAsButton = false,
  width = "calc((100% - 20px) / 2)",
  iconColor = "footer-blue",
  hoverBorderEnabled = true,
  additionalOnClickHandler = () => {},
  analytics,
}: DetailedCardProps) {
  const cardRef = createRef<HTMLLIElement>();
  const cardAnchorRef = createRef<HTMLAnchorElement>();
  const isDeliveryBookingJourney =
    ariaLabel === "Deliveries" || ariaLabel === "Export";

  return (
    <li style={{ listStyleType: "none", width }} ref={cardRef}>
      <Anchor
        href={link}
        target={isDeliveryBookingJourney ? "_top" : "_blank"}
        rel="noreferrer"
        ref={cardAnchorRef}
        aria-label={ariaLabel}
      >
        <StyledCard
          width="100%"
          height="100%"
          hoverBorderEnabled={hoverBorderEnabled}
          onClick={additionalOnClickHandler}
          analytics={analytics}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="left"
              color="#1D2948"
              css={`
                margin: 0px;
                line-height: 1.2;
              `}
            >
              {headline}
            </Typography>
            <Icon
              name={icon}
              color={iconColor}
              weight="solid"
              width={22}
              height={22}
              m={0}
            />
          </Box>
          {children}
          {showLinkAsButton && <Button text={label} variant="primary" />}
          {!showLinkAsButton && (
            <Box display="inline-flex" alignItems="center">
              <Typography
                variant="body-lg"
                color="primary"
                my={0}
                css="cursor:pointer"
              >
                {label}
              </Typography>
              <Icon
                name="arrow-right"
                weight="regular"
                height={18}
                width={18}
                ml={3}
              />
            </Box>
          )}
        </StyledCard>
      </Anchor>
    </li>
  );
}
